import React, { forwardRef, Fragment } from 'react';
import { styled } from '@compiled/react';
import { Box } from '@atlaskit/primitives';
import { AIContainer } from '@atlassian/jira-atlassian-intelligence';
import { defaultComponents } from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller';
import { fireScreenAnalyticsDeferred, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import { useMinimizableModal, TransitionState } from '../../../controllers/use-minimizable-modal';
import type { AiEnabledProps } from '../../../types';
import ScrollContainer from './scroll-container';

export const AiEnabledMiniBody = ({ children }: AiEnabledProps) => (
	<ScrollContainer testId="minimizable-modal.ui.modal-container.ai-enabled-mini.scroll-container">
		<Box paddingBlock="space.100" paddingInline="space.300">
			{children}
		</Box>
	</ScrollContainer>
);

export const AiEnabledMini = forwardRef<HTMLDivElement, AiEnabledProps>(
	({ children, components }: AiEnabledProps, ref) => {
		const { Header = Fragment, Footer = Fragment } = components || {};

		const { isTransitionInState } = useMinimizableModal();
		const isViewEntered = isTransitionInState(['ai-enabled'], TransitionState.ENTERED);

		return (
			<JiraBottomRightCornerOutlet orderFromRight={3} id="issue-create-mini-modal">
				<ShortcutScope>
					<AIContainer
						ref={ref}
						components={{ ContentComponent }}
						isShadow
						shouldFitContainer
						spacing="none"
						testId={!isViewEntered ? undefined : 'minimizable-modal.ui.modal-container.ai-enabled'}
					>
						<MountEvent onMount={fireScreenAnalyticsDeferred} />
						{components ? (
							<>
								<Header />
								<AiEnabledMiniBody>{children}</AiEnabledMiniBody>
								<Footer />
							</>
						) : (
							<>{children}</>
						)}
					</AIContainer>
				</ShortcutScope>
			</JiraBottomRightCornerOutlet>
		);
	},
);

const OriginalContentComponent = defaultComponents.ContentComponent;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentComponent = styled(OriginalContentComponent)({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexDirection: 'column' as const,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	position: 'relative' as const,
	width: '436px',
	height: '569px',
	overflow: 'hidden',
	padding: 0,
});
